/*
 * Delivery Pin — adjust-pin page controller (delivery-pin feature, PR 3 of 3).
 *
 * Flow:
 *   1. Read the one-time token (stripped from the URL into window.__deliveryPinToken
 *      by layout/theme.liquid; falls back to reading ?t= here just in case).
 *   2. POST it to the internal-platform adjust/lookup route to hydrate the map
 *      (410 => expired panel).
 *   3. Load the Google Maps JS API (public referrer-restricted client key) and
 *      show a fixed centre pin; the map pans beneath it.
 *   4. "Save location" POSTs the map-centre coordinates to adjust/save.
 *
 * Security notes: the token lives only in memory and the POST body (never the
 * URL after step 1); fetches use credentials:'omit' so no storefront cookies
 * leave the origin; the page's referrer policy (set in theme.liquid) sends only
 * the origin, never the path/query.
 */
(function () {
  var root = document.getElementById("delivery-pin");
  if (!root) return;

  var API_BASE = (root.dataset.apiBase || "https://internal.endless.ae").replace(/\/+$/, "");
  var MAPS_KEY = (root.dataset.mapsKey || "").trim();
  var DEFAULT_CENTER = {
    lat: parseFloat(root.dataset.defaultLat) || 25.2048,
    lng: parseFloat(root.dataset.defaultLng) || 55.2708,
  };
  var ZOOM = parseInt(root.dataset.zoom, 10) || 16;
  var WHATSAPP = (root.dataset.whatsapp || "").trim();

  var panels = {};
  root.querySelectorAll("[data-panel]").forEach(function (el) {
    panels[el.getAttribute("data-panel")] = el;
  });
  var addressEl = root.querySelector('[data-role="address"]');
  var mapEl = root.querySelector('[data-role="map"]');
  var saveBtn = root.querySelector('[data-role="save"]');
  var saveErrEl = root.querySelector('[data-role="save-error"]');
  var locateBtn = root.querySelector('[data-role="locate"]');
  var locateLabel = root.querySelector('[data-role="locate-label"]');

  var currentCenter = DEFAULT_CENTER;

  /* ---- helpers --------------------------------------------------------- */

  function show(name) {
    Object.keys(panels).forEach(function (key) {
      panels[key].hidden = key !== name;
    });
  }

  function setSaveError(message) {
    if (!saveErrEl) return;
    if (message) {
      saveErrEl.textContent = message;
      saveErrEl.hidden = false;
    } else {
      saveErrEl.textContent = "";
      saveErrEl.hidden = true;
    }
  }

  function wireWhatsapp() {
    if (!WHATSAPP) return;
    root.querySelectorAll('[data-role="whatsapp-expired"], [data-role="whatsapp-nolink"], [data-role="whatsapp-error"]').forEach(function (a) {
      a.setAttribute("href", WHATSAPP);
      a.hidden = false;
    });
  }

  // A reload or reopened tab has no token (it is stripped from the URL on
  // load). This flag, in the tab only and never the token itself, lets such a
  // page say "saved" after a successful save instead of "expired".
  var SAVED_KEY = "delivery_pin_saved";

  function rememberSaved() {
    try {
      window.sessionStorage.setItem(SAVED_KEY, "1");
    } catch (e) {
      /* storage blocked: a reload falls back to the "open the link" panel */
    }
  }

  function clearSaved() {
    try {
      window.sessionStorage.removeItem(SAVED_KEY);
    } catch (e) {
      /* no-op */
    }
  }

  function wasSaved() {
    try {
      return window.sessionStorage.getItem(SAVED_KEY) === "1";
    } catch (e) {
      return false;
    }
  }

  function getTokenFromUrl() {
    try {
      return new URL(window.location.href).searchParams.get("t");
    } catch (e) {
      return null;
    }
  }

  function stripTokenFromUrl() {
    try {
      var u = new URL(window.location.href);
      if (u.searchParams.has("t")) {
        u.searchParams.delete("t");
        window.history.replaceState(null, "", u.pathname + u.search + u.hash);
      }
    } catch (e) {
      /* no-op */
    }
  }

  // POST JSON to our own backend. Resolves to {ok, status, data}; never rejects
  // for HTTP errors (only a genuine network failure rejects).
  function postJson(path, body) {
    return fetch(API_BASE + path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "omit",
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return null;
        })
        .then(function (data) {
          return { ok: res.ok, status: res.status, data: data };
        });
    });
  }

  /* ---- Google Maps loader (vanilla port of the fashion-app loader) ----- */

  var mapsPromise = null;
  function loadGoogleMaps(key) {
    if (window.google && window.google.maps && window.google.maps.Map) return Promise.resolve();
    if (mapsPromise) return mapsPromise;
    mapsPromise = new Promise(function (resolve, reject) {
      if (!key) {
        reject(new Error("no maps key"));
        return;
      }
      var whenReady = function () {
        // With loading=async the global can lag the load event slightly.
        if (window.google && window.google.maps && window.google.maps.Map) resolve();
        else setTimeout(whenReady, 50);
      };
      var existing = document.getElementById("eds-gmaps");
      if (existing) {
        existing.addEventListener("load", whenReady);
        existing.addEventListener("error", function () {
          reject(new Error("maps load error"));
        });
        return;
      }
      var script = document.createElement("script");
      script.id = "eds-gmaps";
      script.src = "https://maps.googleapis.com/maps/api/js?key=" + encodeURIComponent(key) + "&loading=async";
      script.async = true;
      script.onload = whenReady;
      script.onerror = function () {
        reject(new Error("maps load error"));
      };
      document.head.appendChild(script);
    });
    return mapsPromise;
  }

  /* ---- map ------------------------------------------------------------- */

  function initMap(center) {
    var map = new window.google.maps.Map(mapEl, {
      center: center,
      zoom: ZOOM,
      disableDefaultUI: true,
      clickableIcons: false,
      gestureHandling: "greedy",
    });
    currentCenter = center;

    // On every settle, the map centre is where the pin points.
    map.addListener("idle", function () {
      var c = map.getCenter();
      if (!c) return;
      currentCenter = { lat: c.lat(), lng: c.lng() };
    });

    if (locateBtn) {
      locateBtn.addEventListener("click", function () {
        if (!navigator.geolocation) return;
        var reset = function (label) {
          locateBtn.disabled = false;
          if (locateLabel) locateLabel.textContent = label;
        };
        locateBtn.disabled = true;
        if (locateLabel) locateLabel.textContent = "Locating…";
        navigator.geolocation.getCurrentPosition(
          function (pos) {
            map.panTo({ lat: pos.coords.latitude, lng: pos.coords.longitude }); // triggers idle
            map.setZoom(Math.max(ZOOM, 16));
            reset("Use my location");
          },
          function () {
            // Denial/failure is expected; keep the current pin, no crash.
            reset("Use my location");
          },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      });
    }
  }

  function onSave() {
    if (saveBtn.disabled) return;
    saveBtn.disabled = true;
    setSaveError("");
    postJson("/api/webhooks/delivery-pin/adjust/save", {
      token: token,
      coordinates: { lat: currentCenter.lat, lng: currentCenter.lng },
    })
      .then(function (res) {
        if (res && res.ok) {
          rememberSaved();
          show("success");
          return;
        }
        if (res && res.status === 410) {
          show("expired");
          return;
        }
        if (res && res.status === 400) {
          setSaveError((res.data && res.data.error) || "Please choose a spot inside the UAE.");
          saveBtn.disabled = false;
          return;
        }
        setSaveError("We couldn't save that. Please try again.");
        saveBtn.disabled = false;
      })
      .catch(function () {
        setSaveError("We couldn't save that. Please try again.");
        saveBtn.disabled = false;
      });
  }

  /* ---- boot ------------------------------------------------------------ */

  wireWhatsapp();

  var token = window.__deliveryPinToken || getTokenFromUrl();
  stripTokenFromUrl();
  // Once read, drop the global copy so it isn't left hanging around.
  try {
    delete window.__deliveryPinToken;
  } catch (e) {
    window.__deliveryPinToken = undefined;
  }

  if (!token) {
    show(wasSaved() ? "success" : "nolink");
    return;
  }
  // A link with a token is a fresh attempt, so an old saved flag no longer applies.
  clearSaved();

  if (saveBtn) saveBtn.addEventListener("click", onSave);

  postJson("/api/webhooks/delivery-pin/adjust/lookup", { token: token })
    .then(function (res) {
      if (!res || res.status === 410) {
        show("expired");
        return;
      }
      if (!res.ok || !res.data || res.data.status !== "ok") {
        show("error");
        return;
      }
      var center = res.data.suggested || DEFAULT_CENTER;
      if (res.data.address_display && addressEl) {
        addressEl.textContent = res.data.address_display;
        addressEl.hidden = false;
      }
      show("map");
      loadGoogleMaps(MAPS_KEY)
        .then(function () {
          initMap(center);
        })
        .catch(function () {
          show("error");
        });
    })
    .catch(function () {
      show("error");
    });
})();
